// Types
import type { ComponentPropsWithoutRef, Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useDeepCompareEffect } from 'react-use';

import clsx from 'clsx';
import { dequal } from 'dequal';
import type { UseMultipleSelectionProps } from 'downshift';
import { useCombobox, useMultipleSelection } from 'downshift';
import { some, uniqBy } from 'lodash';
import { matchSorter } from 'match-sorter';

import useTranslation from '@/utils/hooks/translation/useTranslation';

//components
import Icon from '@/components/base/Icon';

function defaultOptionFilterFunc(items: ComboboxItemType[], inputValue: string) {
    return matchSorter(items, inputValue.trim().toLowerCase(), { keys: ['label', 'value'] });
}

function defaultItemRenderer(selected: ComboboxItemType) {
    return selected.label;
}

function getItemsWithCreateOption(items: ComboboxItemType[], inputValue: string): InputItemsType {
    const hasExactMatch = () =>
        some(items, (item) => item.value.trim().toLowerCase() === inputValue.trim().toLowerCase());

    if (!inputValue || hasExactMatch()) {
        return { items, isCreating: false };
    }

    const createOption: ComboboxItemType = {
        label: inputValue.toLowerCase(),
        value: inputValue.toLowerCase()
    };

    // If no items at all, show ONLY the create option
    if (items.length === 0) {
        return {
            items: [createOption],
            isCreating: true
        };
    }

    // Items exist but no exact match - add create option to the list
    return {
        items: uniqBy([...items, createOption], 'value'),
        isCreating: true
    };
}

const styleInput = {
    ['default']: {
        container: 'bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue space-x-2 max-h-12',
        input: 'placeholder-icoBlue-400 text-xs font-GSemiBold'
    }
};

const containerClass =
    'relative border-0 w-full flex items-center border transition transition-all px-2.5';
const inputClass = 'relative peer z-1 px-0 py-2 bg-transparent border-none w-full focus:ring-0';

type ComboboxItemType = { label: string; value: string };
type InputItemsType = { items: ComboboxItemType[]; isCreating?: boolean };
type MultiComboboxProps = UseMultipleSelectionProps<ComboboxItemType> & {
    items: ComboboxItemType[];
    optionFilterFunc?: typeof defaultOptionFilterFunc;
    itemRenderer?: typeof defaultItemRenderer;
    placeholder?: string;
    className?: string;
    type?: 'labels';
    onCreateItem: (item: ComboboxItemType) => void;
    comboboxDisclaimer?: string;
    noOptionsText?: string;
    beforeInput?: ReactNode;
    inputStyle?: keyof typeof styleInput;
    onSearch?: (searchValue: string) => boolean;
};

export const MultiCombobox = (props: MultiComboboxProps) => {
    const {
        items,
        optionFilterFunc = defaultOptionFilterFunc,
        itemRenderer = defaultItemRenderer,
        placeholder,
        className,
        type = 'labels',
        onCreateItem,
        comboboxDisclaimer,
        noOptionsText,
        beforeInput,
        inputStyle = 'default',
        onSearch,
        ...downshiftProps
    } = props;

    const { getDropdownProps, addSelectedItem, selectedItems } =
        useMultipleSelection<ComboboxItemType>(downshiftProps);

    const selectedItemValues = selectedItems.map((item) => item.value);

    const [inputValue, setInputValue] = useState('');
    const [inputItems, setInputItems] = useState<InputItemsType>({
        items,
        isCreating: false
    });

    const disclosureRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const { styles, attributes, forceUpdate } = usePopper(
        disclosureRef.current,
        popoverRef.current,
        {
            placement: 'bottom',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 0]
                    }
                }
            ]
        }
    );

    const {
        isOpen,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        selectItem,
        reset
    } = useCombobox({
        inputValue,
        selectedItem: undefined,
        items: inputItems.items,
        defaultHighlightedIndex: 0,
        onInputValueChange: ({ inputValue }) => {
            onSearch?.(inputValue);

            // Client-side filtering: filter items and add create option if needed
            const filteredItems = optionFilterFunc(items, inputValue);
            setInputItems(getItemsWithCreateOption(filteredItems, inputValue));
        },
        stateReducer: (state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;
            switch (type) {
                case useCombobox.stateChangeTypes.InputBlur:
                    reset();
                    return {
                        ...changes,
                        isOpen: false
                    };
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        highlightedIndex: state.highlightedIndex,
                        inputValue,
                        isOpen: state.isOpen
                    };
                case useCombobox.stateChangeTypes.FunctionSelectItem:
                    return {
                        ...changes,
                        inputValue,
                        isOpen: false
                    };
                default:
                    return changes;
            }
        },
        onStateChange: ({ inputValue, type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputChange:
                    setInputValue(inputValue);
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick: {
                    if (
                        !selectedItemValues.includes(selectedItem.value) &&
                        selectedItem?.value?.trim()
                    ) {
                        //If item doesn't exist create in inputItems create
                        if (
                            inputItems.isCreating &&
                            inputItems?.items?.filter((item) => item.label === selectedItem.label)
                                .length === 0
                        ) {
                            onCreateItem(selectedItem);
                        }
                        //Add existing item
                        else {
                            addSelectedItem(selectedItem);
                        }

                        onSearch?.(undefined);
                        setInputItems({ items: items, isCreating: false });
                        setInputValue('');
                        selectItem(null);
                    }
                    break;
                }
                default:
                    break;
            }
        }
    });

    useDeepCompareEffect(() => {
        setInputItems((prev) => ({ ...prev, items: uniqBy([...prev.items, ...items], 'value') }));
    }, [items]);

    useEffect(() => {
        if (forceUpdate) {
            forceUpdate();
        }
    }, []);

    return (
        <div className={clsx('relative flex flex-col', className)}>
            <div className="flex items-center">
                <div
                    className={clsx(
                        containerClass,
                        styleInput[inputStyle].container ?? styleInput['default'].container,
                        { 'rounded-2xl': !isOpen, 'rounded-t-2xl': isOpen }
                    )}
                    ref={disclosureRef}>
                    {beforeInput ? (
                        <div className="z-1 flex items-center text-xs">{beforeInput}</div>
                    ) : null}

                    <input
                        data-testid="combobox-input"
                        {...getInputProps(
                            getDropdownProps({
                                className: `${inputClass} ${
                                    styleInput[inputStyle].input ?? styleInput['default'].input
                                }`,
                                placeholder
                            })
                        )}
                    />
                    {inputValue ? (
                        <button
                            aria-label="clear"
                            className="z-1 flex items-center text-xs !ring-offset-0 focus:ring-1"
                            onClick={() => {
                                setInputValue('');
                                setInputItems({ items, isCreating: false });
                            }}>
                            <Icon icon="close" />
                        </button>
                    ) : null}
                </div>
            </div>
            <div
                style={styles.popper}
                {...attributes.popper}
                {...getMenuProps({
                    ref: popoverRef,
                    className: 'w-full z-1 overflow-hidden text-xs text-icoBlue'
                })}>
                {isOpen ? (
                    <div className="relative rounded-b border-b border-l border-r border-icoGray-300 bg-white dark:border-transparent dark:bg-icoDarkMode-deepBlack">
                        {comboboxDisclaimer ? (
                            <p className="p-2 text-xxs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {comboboxDisclaimer}
                            </p>
                        ) : null}
                        <ul className="scrollbar relative max-h-40">
                            {inputItems?.items.map((item, index) => {
                                if (!selectedItemValues.includes(item.value)) {
                                    //display the create option in li
                                    if (
                                        item?.value.toLowerCase() === inputValue.toLowerCase() &&
                                        inputItems?.isCreating
                                    ) {
                                        return (
                                            <ComboboxItem
                                                key={item.value}
                                                item={item}
                                                index={index}
                                                inputValue={inputValue}
                                                type={type}
                                                highlightedIndex={highlightedIndex}
                                                variant="isCreating"
                                                isCreating={inputItems.isCreating}
                                                getItemProps={getItemProps}
                                                aria-label={`create-${inputValue}`}
                                            />
                                        );
                                    } else {
                                        return (
                                            <ComboboxItem
                                                key={item.value}
                                                item={item}
                                                index={index}
                                                inputValue={inputValue}
                                                type={type}
                                                variant="alreadyExist"
                                                highlightedIndex={highlightedIndex}
                                                getItemProps={getItemProps}
                                                aria-label={
                                                    item?.value.toLowerCase() ===
                                                    inputValue.toLowerCase()
                                                        ? 'exact-match'
                                                        : `option-available-${index}`
                                                }
                                                itemRenderer={itemRenderer}
                                            />
                                        );
                                    }
                                } else {
                                    //if the users try to add an options that already exist and doesn't have any options that match the selection
                                    return (
                                        <ComboboxItem
                                            key={item.value}
                                            item={item}
                                            index={index}
                                            inputValue={inputValue}
                                            type={type}
                                            variant="alreadyAdded"
                                            highlightedIndex={highlightedIndex}
                                            getItemProps={getItemProps}
                                            aria-label={`disabled-${item.value}`}
                                            itemRenderer={itemRenderer}
                                        />
                                    );
                                }
                            })}
                            {/* if selectedItem >= items display message saying that the user  */}
                            {items?.length <= selectedItemValues?.length &&
                            !inputValue &&
                            noOptionsText ? (
                                <li className="pointer-events-none sticky bottom-0 rounded-b border-t border-icoGray-300 bg-white py-2 text-center text-xxs text-icoGray-500 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf">
                                    {noOptionsText}
                                </li>
                            ) : null}
                        </ul>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const variants = {
    isCreating:
        'bg-white dark:bg-icoDarkMode-deepBlack rounded-b focus:bg-icoBlue-200 dark:focus:bg-icoBlue/20 border-t border-icoGray-300 dark:border-icoDarkMode-beton sticky bottom-0',
    alreadyAdded: 'pointer-events-none text-icoGray-500 dark:text-icoDarkMode-wolf',
    alreadyExist: ''
};

type ComboboxItemProps = ComponentPropsWithoutRef<'p'> & {
    item: ComboboxItemType;
    index: number;
    inputValue: string;
    type: MultiComboboxProps['type'];
    highlightedIndex?: number;
    variant: keyof typeof variants;
    isCreating?: boolean;
    itemRenderer?: MultiComboboxProps['itemRenderer'];
    getItemProps: ReturnType<typeof useCombobox>['getItemProps'];
};
const ComboboxItem = ({
    item,
    index,
    inputValue,
    type,
    highlightedIndex,
    variant,
    isCreating,
    getItemProps,
    itemRenderer,
    ...restProps
}: ComboboxItemProps) => {
    const { t } = useTranslation('commons');

    return (
        <li
            className={clsx('p-2 text-xs hover:cursor-pointer', variants[variant] ?? '', {
                'bg-icoBlue-100 dark:bg-icoBlue/20':
                    highlightedIndex === index && variant === 'alreadyExist',
                'bg-icoGray-100 dark:bg-icoDarkMode-wolf/20':
                    highlightedIndex === index && variant === 'alreadyAdded'
            })}
            key={`${item.value}${index}`}
            {...getItemProps({ item, index })}>
            <p className="flex items-center space-x-2" {...restProps}>
                {isCreating ? (
                    <span className="text-icoBlack dark:text-white">{t('create')}</span>
                ) : null}
                {type ? <Icon icon={type} /> : null}
                <span>{itemRenderer ? itemRenderer(item) : inputValue}</span>
            </p>
        </li>
    );
};

type WrapperMultiComboBoxProps = UseMultipleSelectionProps<ComboboxItemType> &
    Pick<
        MultiComboboxProps,
        'placeholder' | 'beforeInput' | 'comboboxDisclaimer' | 'noOptionsText' | 'onSearch'
    > & {
        initialItems: ComboboxItemType[];
        selectedItems: ComboboxItemType[];
        setSelectedItems: Dispatch<SetStateAction<ComboboxItemType[]>>;
    };
//Helper wrapper that abstract createItem and selectItem handlers
export const WrapperMultiComboBox = ({
    placeholder,
    beforeInput,
    comboboxDisclaimer,
    noOptionsText,
    initialItems,
    selectedItems,
    setSelectedItems,
    onSearch,
    ...restProps
}: WrapperMultiComboBoxProps) => {
    const [pickerItems, setPickerItems] = useState(initialItems);

    useEffect(() => {
        if (!dequal(pickerItems, initialItems)) setPickerItems(initialItems);
    }, [initialItems]);
    const handleCreateItem = (item: ComboboxItemType) => {
        setPickerItems((curr) => [...curr, item]);
        setSelectedItems((curr) => [...curr, item]);
    };

    const handleSelectedItemsChange = (selectedItems: ComboboxItemType[]) => {
        if (selectedItems) {
            setSelectedItems(selectedItems);
        }
    };

    return (
        <MultiCombobox
            beforeInput={beforeInput}
            placeholder={placeholder}
            onCreateItem={handleCreateItem}
            comboboxDisclaimer={comboboxDisclaimer}
            noOptionsText={noOptionsText}
            items={pickerItems}
            selectedItems={selectedItems}
            onSearch={onSearch}
            onSelectedItemsChange={(changes) => {
                //add this condition to block the build in ability to remove selected items
                //with backspace when input is empty
                if (
                    changes.type === '__dropdown_keydown_backspace__' ||
                    !changes?.selectedItems?.length
                ) {
                    return;
                }
                handleSelectedItemsChange(changes.selectedItems);
            }}
            {...restProps}
        />
    );
};

export type ComboboxProps = Pick<
    WrapperMultiComboBoxProps,
    'beforeInput' | 'initialItems' | 'selectedItems' | 'setSelectedItems'
> & {
    noOptionsTextLocale?: WrapperMultiComboBoxProps['noOptionsText'];
    comboboxDisclaimerLocale?: WrapperMultiComboBoxProps['comboboxDisclaimer'];
    onSearch?: (searchValue: string) => boolean;
};
export const Combobox = ({
    initialItems = [],
    selectedItems,
    setSelectedItems,
    noOptionsTextLocale = 'no_label_available',
    comboboxDisclaimerLocale = 'select_label_or_create',
    onSearch
}: ComboboxProps) => {
    const { t } = useTranslation('commons');

    return (
        <WrapperMultiComboBox
            beforeInput={<Icon icon="labels" />}
            comboboxDisclaimer={t(comboboxDisclaimerLocale)}
            noOptionsText={t(noOptionsTextLocale)}
            initialItems={initialItems}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            onSearch={onSearch}
        />
    );
};
